import React, { useState, useEffect, useCallback } from "react";
import Layout from "../Layout";
import { Link, useNavigate } from "react-router-dom";
import { BiChevronLeft, BiChevronRight, BiPlus } from "react-icons/bi";
import { toast } from "react-hot-toast";
import axios from "axios";
import { BaseUrl } from "../BaseUrl";

function Orders() {
  const navigate = useNavigate();

  // Orders data state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const itemsPerPage = 10;

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      const response = await axios.get(
        `${BaseUrl}/facility/orders?${params.toString()}`,
        {
          withCredentials: true,
        }
      );

      console.log("Orders API Response:", response.data);

      const responseData = response.data?.data || response.data?.orders || [];

      if (Array.isArray(responseData)) {
        const transformedOrders = responseData.map((order, index) => {
          // Generate Order ID
          const orderId =
            order.orderId || `ORD-${String(order._id).slice(-4).toUpperCase()}`;

          // Get patient info
          const patientName =
            order.patientId?.fullName || order.patientName || "Unknown Patient";
          const patientInitials = patientName
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);

          // Get address - prioritize deliveryAddress from order
          const address =
            order.deliveryAddress ||
            order.patientId?.address?.fullAddress ||
            order.address ||
            order.patientId?.contactDetails?.address ||
            "Address not available";

          // Calculate amount from medications or use provided amount
          let amount = order.amount || order.totalAmount || 0;
          if (!amount && order.medications) {
            amount = order.medications.reduce(
              (sum, med) => sum + (med.price || 0),
              0
            );
          }

          return {
            id: order._id || index + 1,
            _id: order._id,
            orderId: orderId,
            patientName: patientName,
            patientInitials: patientInitials,
            patientImage: order.patientId?.userImage || null,
            address:
              typeof address === "object"
                ? `${address.street || ""}, ${address.city || ""}`.trim()
                : address,
            amount: amount,
            date: order.createdAt
              ? new Date(order.createdAt).toLocaleDateString("en-CA") // YYYY-MM-DD format
              : "N/A",
            createdAt: order.createdAt,
          };
        });

        setOrders(transformedOrders);
        setTotalOrders(response.data?.total || transformedOrders.length);
        setTotalPages(
          response.data?.totalPages ||
            Math.ceil(transformedOrders.length / itemsPerPage)
        );
      } else {
        setOrders([]);
        setTotalOrders(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error(error.response?.data?.message || "Error loading orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Navigate to order details
  const viewOrderDetails = (orderId) => {
    navigate(`/orders/preview/${orderId}`);
  };

  // Get random color for avatar based on initials
  const getAvatarColor = (initials) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-yellow-500",
      "bg-red-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-teal-500",
    ];
    const index = initials.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalOrders);

  return (
    <Layout>
      {/* Floating Add Button */}
      <Link
        to='/orders/create'
        className='flex-colo button-fb fixed bottom-8 right-12 z-50 h-16 w-16 animate-bounce rounded-full border border-border bg-[#0095D9] text-white'
      >
        <BiPlus className='text-2xl' />
      </Link>

      {/* Header */}
      <div className='mb-6 flex items-center gap-4'>
        <button
          onClick={() => navigate(-1)}
          className='flex h-10 w-10 items-center justify-center rounded-lg border border-border hover:bg-dry'
        >
          <BiChevronLeft className='text-xl' />
        </button>
        <h1 className='text-xl font-semibold'>Order lists</h1>
      </div>

      {/* Orders Table */}
      <div className='overflow-hidden rounded-xl border border-border bg-white'>
        <div className='w-full overflow-x-auto'>
          {loading ? (
            <div className='flex items-center justify-center py-20'>
              <div className='h-10 w-10 animate-spin rounded-full border-b-2 border-[#0095D9]'></div>
            </div>
          ) : orders.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-20'>
              <p className='text-lg text-textGray'>No orders yet</p>
              <p className='mt-2 text-sm text-textGray'>
                Orders from prescriptions will appear here
              </p>
            </div>
          ) : (
            <>
              <table className='w-full table-auto'>
                <thead className='border-b border-border bg-dry'>
                  <tr>
                    <th className='px-6 py-4 text-left text-xs font-semibold text-textGray'>
                      #
                    </th>
                    <th className='px-6 py-4 text-left text-xs font-semibold text-textGray'>
                      Order ID
                    </th>
                    <th className='px-6 py-4 text-left text-xs font-semibold text-textGray'>
                      Date
                    </th>
                    <th className='px-6 py-4 text-left text-xs font-semibold text-textGray'>
                      Patient
                    </th>
                    <th className='px-6 py-4 text-left text-xs font-semibold text-textGray'>
                      Address
                    </th>
                    <th className='px-6 py-4 text-right text-xs font-semibold text-textGray'>
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr
                      key={order._id}
                      onClick={() => viewOrderDetails(order._id)}
                      className='cursor-pointer border-b border-border transition hover:bg-greyed'
                    >
                      <td className='px-6 py-4 text-sm text-textGray'>
                        {startIndex + index}
                      </td>
                      <td className='px-6 py-4 text-sm font-medium text-main'>
                        {order.orderId}
                      </td>
                      <td className='px-6 py-4 text-sm text-textGray'>
                        {order.date}
                      </td>
                      <td className='px-6 py-4'>
                        <div className='flex items-center gap-3'>
                          {order.patientImage ? (
                            <img
                              src={order.patientImage}
                              alt={order.patientName}
                              className='h-10 w-10 rounded-full object-cover'
                            />
                          ) : (
                            <div
                              className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium text-white ${getAvatarColor(order.patientInitials)}`}
                            >
                              {order.patientInitials}
                            </div>
                          )}
                          <span className='text-sm font-medium text-main'>
                            {order.patientName}
                          </span>
                        </div>
                      </td>
                      <td className='max-w-[200px] truncate px-6 py-4 text-sm text-textGray'>
                        {order.address}
                      </td>
                      <td className='px-6 py-4 text-right text-sm font-medium text-main'>
                        ₹{order.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className='flex items-center justify-between border-t border-border px-6 py-4'>
                <p className='text-sm text-textGray'>
                  Showing {startIndex}-{endIndex} of {totalOrders} orderlists
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex h-8 w-8 items-center justify-center rounded border ${
                      currentPage === 1
                        ? "cursor-not-allowed border-gray-200 text-gray-300"
                        : "border-border text-main hover:bg-dry"
                    }`}
                  >
                    <BiChevronLeft className='text-lg' />
                  </button>
                  <span className='rounded bg-[#0095D9] bg-opacity-10 px-3 py-1 text-sm font-medium text-main'>
                    {currentPage}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className={`flex h-8 w-8 items-center justify-center rounded border ${
                      currentPage >= totalPages
                        ? "cursor-not-allowed border-gray-200 text-gray-300"
                        : "border-border text-main hover:bg-dry"
                    }`}
                  >
                    <BiChevronRight className='text-lg' />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Orders;
